const debug = require("debug")("terrain-tracker");

// What the route is called when it is drawn on paper. He is reading a map at a bar, so nothing
// here knows what the country feels like underfoot — no peaks are forbidding and no torrents
// twist, because he has not been there yet. `water` is `height <= seaLevel` (lib/util/path.js),
// which on a walking route is a lake or a shallows and not the "seas" it used to be called.
const TERRAIN_NAMES = {
  plain: "flat country",
  hill: "hills",
  mountain: "mountains",
  river: "a river",
  water: "water",
};

// The same terrain as a mass noun, for the sentence about which one there is most of.
const TERRAIN_BULK = {
  plain: "flat country",
  hill: "hills",
  mountain: "mountains",
  river: "river country",
  water: "water",
};

// `near river` is a cell neighbouring a river cell (lib/util/path.js), so it is the approach to
// the same water the `river` step crosses. One feature on a map, and folding it in keeps the
// line from reading `the river's edge, then the river`.
const TERRAIN_ALIASES = { "near river": "river" };

const NUMBERS = { 1: "one", 2: "two", 3: "three", 4: "four", 5: "five" };

class TerrainTracker {
  constructor(features, step = 0) {
    this.features = features;
    this.currentStep = step;
    this.previousFeature = null;
    this.context = {
      inWater: false,
      inRiver: false,
      nearRiver: false,
      inMountain: false,
      onHill: false,
      onPlain: false,
    };
    this.events = {
      enteredRiver: false,
      leftRiver: false,
      enteredNearRiver: false,
      leftNearRiver: false,
      enteredMountain: false,
      leftMountain: false,
      enteredHill: false,
      leftHill: false,
      enteredPlain: false,
      leftPlain: false,
      enteredWater: false,
      leftWater: false,
    };
    debug(this.features);
  }

  // Update the context based on the current terrain feature
  updateContext() {
    const feature = this.features[this.currentStep];
    debug(feature, this.previousFeature, this.events);
    this.context = {
      inRiver: feature === "river",
      nearRiver: feature === "near river",
      inMountain: feature === "mountain",
      onHill: feature === "hill",
      onPlain: feature === "plain",
      inWater: feature === "water",
    };
    this.updateEvents(feature);
  }

  // Update events based on the transition between terrain features
  updateEvents(currentFeature) {
    this.events = {
      enteredRiver: currentFeature === "river" &&
        this.previousFeature !== "river",
      leftRiver: this.previousFeature === "river" && currentFeature !== "river",
      enteredNearRiver: currentFeature === "near river" &&
        this.previousFeature !== "near river",
      leftNearRiver: this.previousFeature === "near river" &&
        currentFeature !== "near river",
      enteredMountain: currentFeature === "mountain" &&
        this.previousFeature !== "mountain",
      leftMountain: this.previousFeature === "mountain" &&
        currentFeature !== "mountain",
      enteredHill: currentFeature === "hill" && this.previousFeature !== "hill",
      leftHill: this.previousFeature === "hill" && currentFeature !== "hill",
      enteredPlain: currentFeature === "plain" &&
        this.previousFeature !== "plain",
      leftPlain: this.previousFeature === "plain" && currentFeature !== "plain",
      enteredWater: currentFeature === "water" &&
        this.previousFeature !== "water",
      leftWater: this.previousFeature === "water" && currentFeature !== "water",
    };
    this.previousFeature = currentFeature;
  }

  atStep(step) {
    // features is parallel to route, so every valid index has terrain. It used to be one element
    // short — built per edge rather than per vertex — and this guard was written around that,
    // which left the last steps of the journey reporting the terrain of the one before.
    if (step < this.features.length) {
      this.currentStep = step;
      this.updateContext();
    } else {
      debug("the end of the journey.");
    }
  }

  // Move the protagonist to the next step and update the context
  nextStep() {
    if (this.currentStep < this.features.length - 1) {
      this.currentStep++;
      this.updateContext();
    } else {
      debug("the end of the journey.");
    }
  }

  // Get a description of the current situation and events
  describeSituation() {
    let description = null;

    // Terrain notifications
    Object.entries(this.context).forEach(([terrain, isInTerrain]) => {
      if (isInTerrain) {
        let terrainNotification;
        switch (terrain) {
          case "inWater":
            terrainNotification = "waters deep, cold, relentless.";
            break;
          case "inRiver":
            terrainNotification = "river's flow, strong and unyielding.";
            break;
          case "nearRiver":
            terrainNotification = "river nearby, its murmur constant.";
            break;
          case "inMountain":
            terrainNotification = "among mountains, vast and silent.";
            break;
          case "onHill":
            terrainNotification = "atop a hill, the land sprawls below.";
            break;
          case "onPlain":
            terrainNotification = "vast plains stretch, endless under the sky.";
            break;
          default:
            terrainNotification = null;
        }
        if (terrainNotification) description = terrainNotification;
      }
    });
    if (description) return description;

    // Event notifications
    let left;
    let entered;
    debug(this.events);
    Object.entries(this.events).forEach(([event, happened]) => {
      if (happened) {
        switch (event) {
          case "leftRiver":
            left = "leaving the river";
            break;
          case "leftNearRiver":
            left = "departing the river's side";
            break;
          case "leftMountain":
            left = "descending from the mountains";
            break;
          case "leftHill":
            left = "descending the hill";
            break;
          case "leftPlain":
            left = "leaving the plains";
            break;
          case "leftWater":
            left = "exiting the water";
            break;
          case "enteredRiver":
            entered = "steping into the river, currents against skin.";
            break;
          case "enteredNearRiver":
            entered = "drawing near the river, its murmur constant.";
            break;
          case "enteredMountain":
            entered = "ascending into the mountains, each step a conquest.";
            break;
          case "enteredHill":
            entered = "climbing a hill, the world unfurls at the summit.";
            break;
          case "enteredPlain":
            entered = "steping onto the plains, the earth wide and open.";
            break;
          case "enteredWater":
            entered = "entering the water, a cold embrace.";
            break;
          default:
            debug("no known event.", event);
            break;
        }
      }
    });

    return left ? `${left} and ${entered}` : entered;
  }

  // The order the country arrives in, by first appearance. Six terrains exist, so the whole
  // route fits in one line and nothing has to be dropped to keep it short.
  //
  // No claim is made about where the route *ends*: first-appearance order truthfully says he
  // will meet hills before mountains, but the last name in the list is only the last thing to
  // show up for the first time, which is usually not the country he arrives in.
  sequenceLine(order) {
    const names = order.map((feature) => TERRAIN_NAMES[feature]);
    if (names.length === 1) return `${names[0]} the whole way.`;
    return `${names.join(". then ")}.`;
  }

  /**
   * The map read at the bar, and the player's only preview of the route.
   *
   * Returns lines, not a paragraph. The old version built one string and split it back apart on
   * '.', which cost every sentence the period it was split on, put a leading space on all but
   * the first, and left a trailing '' — and Event.js renders one <div> per entry, so every map
   * screen ended with an empty block.
   *
   * Rare terrain is named because it is on the route, not because it cleared a frequency bar.
   * The old 10%-of-total threshold dropped a one-step river crossing while keeping the plains
   * on either side of it, which is backwards — the crossing is the part worth knowing about.
   * That threshold also made the significance test unfalsifiable: with six terrains the most
   * common one always holds at least a sixth of the steps, so the 'varying whispers' branch it
   * guarded was unreachable.
   *
   * Three lines at most: what is out there and in what order, which one there is most of (the
   * bulk of the route is the bulk of the days), and how many times the river has to be crossed.
   */
  describeJourney() {
    const features = (this.features || [])
      .map((feature) => TERRAIN_ALIASES[feature] || feature)
      .filter((feature) => TERRAIN_NAMES[feature]);

    if (!features.length) return [];

    const counts = features.reduce((acc, feature) => {
      acc[feature] = (acc[feature] || 0) + 1;
      return acc;
    }, {});

    // One pass for both: `order` is each terrain the first time it shows up, `runs` collapses
    // consecutive repeats so a river approached, crossed and left counts as one crossing.
    const order = [];
    const runs = [];
    features.forEach((feature) => {
      if (!order.includes(feature)) order.push(feature);
      if (feature !== runs[runs.length - 1]) runs.push(feature);
    });

    const lines = [this.sequenceLine(order)];

    // Object.keys follows insertion order, which here is first appearance, and >= keeps the
    // earlier of a tie — so the same route always reads the same way.
    const dominant = Object.keys(counts).reduce((a, b) =>
      counts[a] >= counts[b] ? a : b,
    );
    lines.push(
      counts[dominant] / features.length >= 0.5
        ? `most of it is ${TERRAIN_BULK[dominant]}. that is where the days go.`
        : `more ${TERRAIN_BULK[dominant]} than anything else.`,
    );

    const crossings = runs.filter((feature) => feature === "river").length;
    if (crossings) {
      lines.push(
        crossings === 1
          ? "one river crossing."
          : `${NUMBERS[crossings] || crossings} river crossings.`,
      );
    }

    debug(lines);
    return lines;
  }
}

export default TerrainTracker;
